import React, { useEffect, useRef, useState } from "react";

import { useNavigate } from "react-router-dom";
import RBImage from "react-bootstrap/Image";
import { Col, Container, Modal, Row, Spinner } from "react-bootstrap";

import "./take-selfie1.css";

import logo2 from "../../../assets/images/logo2.png";
import addplus from "../../../assets/images/add-plus.png";
import backarrow from "../../../assets/images/backarrow.jpg";
import dont from "../../../assets/images/do.png";
import doo from "../../../assets/images/dont.png";

import Stepper from "../Stepper";
import Loader from "../../Utility/Loader";
import ImageCrop from "../../cropimage/ImageCrop";
import * as userActions from "../../../actions/user";
import { fileUpload } from "../../../actions/uploadActions";
import { useAlert } from "../../../Context/AlertModalContext";
import { getProfileSelfieImageUrls } from "../../../utils/serverUtils";

export const Selfie = () => {
  const alert = useAlert();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [showDosDontsModal, setShowDosDontsModal] = useState(true);
  const [duplicateImageError, setDuplicateImageError] = useState(false);

  const selectedFileHashesRef = useRef({});
  const fileInputRefs = {
    main: useRef(null),
    first: useRef(null),
    second: useRef(null),
  };

  const [images, setImages] = useState({
    main: { key: null, src: null, fileName: null, loading: 0, imageKey: "main", file: null, actionType: null },
    first: { key: null, src: null, fileName: null, loading: 0, imageKey: "first", file: null, actionType: null },
    second: { key: null, src: null, fileName: null, loading: 0, imageKey: "second", file: null, actionType: null },
  });

  const computeHash = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest("SHA-256", arrayBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, "0")).join("");
  };

  // Cropper Variabls
  const cropperRef = useRef(null);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [currentImageKey, setCurrentImageKey] = useState(null);

  const IMAGE_CROP_DIMENSIONS = {
    main: { width: 500, height: 900 },
    first: { width: 600, height: 600 },
    second: { width: 600, height: 600 },
  };
  const { width, height } = IMAGE_CROP_DIMENSIONS[currentImageKey] || { width: 600, height: 600 };

  useEffect(() => {
    const fetchUserImage = async () => {
      try {
        const response = await userActions.getUserMedia();
        if (response) {
          const profileImages = await getProfileSelfieImageUrls(response);

          setImages((prev) => {
            const updatedImages = { ...prev };
            Object.entries(profileImages).forEach(([key, url]) => {
              if (url) {
                updatedImages[key] = {
                  ...updatedImages[key],
                  src: url,
                  actionType: "uploaded"
                };
              }
            });
            return updatedImages;
          });
        }
      } catch (error) {
        console.error("Error fetching user images:", error);
      }
    };

    fetchUserImage();
  }, []);

  const handleClick = imageKey => {
    const input = fileInputRefs[imageKey]?.current;
    if (input) {
      input.value = ""; // <-- Clear the previous file
      input.click();
    }
  };

  const handleFileChange = async (e, imageKey) => {
    const files = e.target?.files;
    if (!files?.length) return;

    const file = files[0];

    // Check for duplicate image by hash
    const fileHash = await computeHash(file);
    const isDuplicate = Object.entries(selectedFileHashesRef.current).some(
      ([key, hash]) => key !== imageKey && hash === fileHash
    );
    if (isDuplicate) {
      setDuplicateImageError(true);
      return;
    }

    setDuplicateImageError(false);

    // Save hash in both ref and state (for rendering)
    selectedFileHashesRef.current[imageKey] = fileHash;

    // Read image as base64 for cropping
    const reader = new FileReader();
    reader.onload = () => {
      setImageToCrop(reader.result);
    };
    reader.readAsDataURL(file);

    setCurrentImageKey(imageKey);
    setShowCropModal(true);
  };

  const handleCropCancel = () => {
    //selectedFileHashesRef.current[currentImageKey] = null; // Reset hash for the clicked image

    setCurrentImageKey(null)
    setShowCropModal(false);
    setImageToCrop(null);
  };

  const handleCropSave = async () => {
    try {
      if (!cropperRef.current) throw new Error("No cropper reference available");

      const croppedBlob = await cropperRef.current.handleSubmit();
      if (!croppedBlob) throw new Error("Cropping failed");

      const croppedFile = new File([croppedBlob], "cropped-image.jpg", {
        type: "image/jpeg",
      });

      const fileURL = URL.createObjectURL(croppedFile);
      setImages((prev) => ({
        ...prev,
        [currentImageKey]: {
          ...prev[currentImageKey],
          croppedFile,
          fileName: croppedFile.name,
          src: fileURL,
          loading: -1,
        },
      }));

      handleCropCancel();

      try {
        const uploadedFile = await fileUpload(croppedFile);
        setImages((prev) => ({
          ...prev,
          [currentImageKey]: {
            ...prev[currentImageKey],
            key: uploadedFile.key,
            loading: 0,
          },
        }));
      } catch (error) {
        setImages((prev) => ({
          ...prev,
          [currentImageKey]: {
            ...prev[currentImageKey],
            loading: 0,
          },
        }));
        alert.setModal({
          show: true,
          title: "Error",
          message: error.message || "Something went wrong. Please try again.",
        });
      }
    } catch (error) {
      console.error("Error saving images:", error);
    }
  }

  const handleNextClick = async () => {
    if (
      images.main.actionType === "uploaded"
      && images.first.actionType === "uploaded"
      && images.second.actionType === "uploaded"
    ) {
      navigate("/located");
      return;
    }

    if (
      !images.main?.key ||
      !images.first?.key ||
      !images.second?.key
    ) {
      alert.setModal({
        show: true,
        message: "Please upload all three images before continuing",
        title: "Error",
      });
      return;
    }

    setLoading(true);

    try {
      await userActions.updateUserMedia({
        main: images.main.key,
        first: images.first.key,
        second: images.second.key,
      });
      navigate("/located");
    } catch (error) {
      console.error("Error saving images:", error);
    } finally {
      setLoading(false);
    }
  };

  const renderImageContent = (img) => (
    <>
      {!img.src ? (
        <>
          <RBImage className="selfie-icon" src={addplus} />
          <span>Add your {img.imageKey} photo</span>
        </>
      ) : (
        <RBImage
          src={img.src}
          className="user-picture1"
          style={{ height: "100%", width: "100%", objectFit: "contain" }}
          alt="Uploaded"
          fluid
        />
      )}
      {img.loading === -1 && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            height: "100%",
            width: "100%",
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            zIndex: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div className="spinner-border text-primary" role="status" />
        </div>
      )}
    </>
  );

  return (
    <div className="selfie-container">
      {loading && <Loader />}
      <div className="selfie-bg"></div>
      <Container className="selfie-main">
        <Container className="logo-progressbar6">
          <Container className="logo-arrow6">
            <RBImage src={backarrow} className="backarrow" onClick={() => window.history.back()} />
            <RBImage src={logo2} alt="Logo" className="logo" style={{ backgroundColor: "transparent" }} />
          </Container>
        </Container>
        <Stepper activeStep="Selfie" />
        <div className="container-div">
          <p className="pic-heading mb-2">Add your photos</p>
          <div className="containerChild1">
            <Container>
              <p className="pic-text" style={{ fontSize: "16px", marginBottom: "12px" }}>
                Use pics of yourself which clearly show your face, your hobbies & more! Check out more
                <spam
                  onClick={() => setShowDosDontsModal(true)}
                  style={{
                    textDecoration: "underline",
                    color: "blue",
                    cursor: "pointer",
                  }}
                > tips here</spam>
                .
              </p>
            </Container>

            <Container className="selfie-adding">
              <Row className="selfie-row" onClick={() => handleClick("main")}>
                <Col md={12} className="selfie-box1 p-0" style={{ height: "24dvh", position: "relative" }}>
                  {renderImageContent(images.main)}
                  <input
                    type="file"
                    ref={fileInputRefs.main}
                    onChange={(e) => handleFileChange(e, "main")}
                    style={{ display: "none" }}
                  />
                </Col>
              </Row>

              <Row className="selfie-row2">
                {["first", "second"].map((key) => (
                  <Col
                    key={key}
                    md={6}
                    className="selfie-box2"
                    style={{ height: "24dvh", position: "relative" }}
                    onClick={() => handleClick(key)}
                  >
                    {renderImageContent(images[key])}
                    <input
                      type="file"
                      ref={fileInputRefs[key]}
                      onChange={(e) => handleFileChange(e, key)}
                      style={{ display: "none" }}
                    />
                  </Col>
                ))}
              </Row>
            </Container>

            {duplicateImageError && (
              <div style={{ color: "red", marginTop: "10px" }}>
                <p>Please add a photo you haven’t already used</p>
              </div>
            )}
          </div>

          <div className="basic-btn">
            <button
              type="submit"
              className="global-next-bottom-fix-btn"
              onClick={handleNextClick}
              disabled={loading}
            >
              {loading ? (
                <Spinner animation="border" size="sm" role="status" />
              ) : (
                "Next"
              )}
            </button>
          </div>
        </div>
      </Container>

      {/* Cropper Modal */}
      <Modal
        centered
        className="customer-modal-without-header-footer crop-modal"
        show={showCropModal}
        onHide={handleCropCancel}
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header>
          <Modal.Title>Crop your photo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ImageCrop
            ref={cropperRef}
            url={imageToCrop}
            cropWidth={width}
            cropHeight={height}
          />
        </Modal.Body>
        <Modal.Footer className="crop-modal-footer">
          <div className="d-flex justify-content-between w-100 gap-2">
            <button className="global-cancel-button" onClick={handleCropCancel}>
              Cancel
            </button>
            <button className="global-next-btn" onClick={handleCropSave}>
              Add Photo
            </button>
          </div>
        </Modal.Footer>
      </Modal>
      <Modal
        centered
        className="customer-modal-without-header-footer dos-donts-modal"
        show={showDosDontsModal}
        onHide={() => setShowDosDontsModal(false)}
      >
        <Modal.Header className="do-donts-head" closeButton></Modal.Header>
        <Modal.Body className="dos-donts-modal-body">
          <div style={{ display: "flex", flexDirection: "column" }}>
            <div
              style={{
                alignItems: "center",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <h5>Do’s</h5>
              <RBImage
                style={{ marginBottom: "-20px", zIndex: "10" }}
                src={doo}
              />
              <ul className="dos">
                <li>
                  <p>
                    <span>Light it up</span>: Use good lighting, natural light
                    is best. Avoid shadows.
                  </p>
                </li>
                <li>
                  <p>
                    <span>Keep it real</span>: Use authentic pics of yourself
                    which clearly show your face.
                  </p>
                </li>
                <li>
                  <p>
                    <span>Show off your personality</span>: Love traveling?
                    Have a special hobby? Don't hesitate to show it off
                    through your pics.
                  </p>
                </li>
              </ul>
            </div>
            <div
              style={{
                alignItems: "center",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <h5>Don’t</h5>
              <RBImage
                style={{ marginBottom: "-20px", zIndex: "10" }}
                src={dont}
              />
              <ul className="dos">
                <li>
                  <p>
                    <span>Hide your face</span>: Ensure your face is clearly
                    visible. Avoid sunglasses or hats that hide your face.
                  </p>
                </li>
                <li>
                  <p>
                    <span>Fake it</span> Avoid heavy filters or overly edited
                    photos. Stay authentic to how you look in real life.
                    You're fabulous as you are!
                  </p>
                </li>
                <li>
                  <p>
                    <span>Add too many pics with others</span>: Group photos
                    can be confusing as members might not know who you are.
                    Solo pics are ideal!
                  </p>
                </li>
              </ul>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};